import { Text, useColorModeValue } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { forwardRef, useCallback, useMemo, useState } from "react";

import { ManagedFormControl } from "@/common/components/Form/ManagedFormControl";
import {
  ListType,
  MultiSelector,
} from "@/common/components/MultiSelector/MultiSelector";
import { UserDisplay } from "@/common/components/UserDisplay";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useUserNamesData } from "@/common/data/useUserNamesData";
import { useUsersSearch } from "@/common/data/useUsers";
import { userQueryKeys } from "@/company/data/userQueryKeys";
import { GroupSummary } from "@/feature/components/AccessSummary";
import { UIFilter, UserIdsFilter } from "@reflag/shared/filter";
import { UserName } from "@reflag/shared/userAPI";

export const UserIdsFilterItem = ({ filter }: { filter: UserIdsFilter }) => {
  const { data: userNames = [] } = useUserNamesData(filter.ids.slice(0, 3));

  const avatarBackgroundColor = useColorModeValue("gray.50", "gray.750");

  return (
    <GroupSummary
      avatarBackgroundColor={avatarBackgroundColor}
      count={filter.ids.length}
      entityType="user"
      sampleEntities={userNames.slice(0, 3)}
    />
  );
};

export const UserIdsFilterFields = () => {
  return (
    <ManagedFormControl
      name="ids"
      render={({ field }) => <UsersSelector {...field} />}
    />
  );
};

type UsersSelectorProps = {
  value: string[];
  onChange: (value: string[]) => void;
};

export const UsersSelector = forwardRef<HTMLInputElement, UsersSelectorProps>(
  ({ value, onChange: onChangeProp }, ref) => {
    const { appId, envId } = useEnvironment();
    const queryClient = useQueryClient();
    const [searchValue, setSearchValue] = useState("");
    const [listType, setListType] = useState<ListType>("all");
    const { data: names = [] } = useUserNamesData(value);

    // todo: remove unknown users when useUsersSearch supports it
    const unknownUsers = useMemo(() => {
      return names.filter(
        (c) => c.isUnknown && (!searchValue || c.id.includes(searchValue)),
      );
    }, [names, searchValue]);

    const {
      data: users = [],
      isFetching,
      isFetchingNextPage,
      hasNextPage,
      fetchNextPage,
    } = useUsersSearch(searchValue, {
      filter:
        listType === "selected"
          ? ({
              type: "userIds",
              ids: value,
            } satisfies UIFilter)
          : undefined,
    });

    const suggestions = useMemo(() => {
      return [...unknownUsers, ...users];
    }, [users, unknownUsers]);

    const onChange = useCallback(
      (userIds: string[], meta?: UserName[]) => {
        if (!onChangeProp) return;

        // Get the current list of items by ids
        const current = queryClient.getQueryData<UserName[]>(
          userQueryKeys.userNames(appId, envId, userIds),
        );

        // Update the list of items by ids
        queryClient.setQueryData<UserName[]>(
          userQueryKeys.userNames(appId, envId, userIds),
          () => {
            return userIds.map(
              (id) =>
                meta?.find((c) => c.id === id) ??
                current?.find((c) => c.id === id) ?? {
                  id,
                  name: null,
                  avatarUrl: null,
                  email: null,
                },
            );
          },
        );

        if (userIds.length === 0) setListType("all");
        onChangeProp(userIds);
      },
      [appId, envId, onChangeProp, queryClient],
    );

    return (
      <MultiSelector
        ref={ref}
        addRawLabel={(searchValue) => (
          <>
            Add user ID{" "}
            <Text as="span" fontWeight="bold">
              {searchValue}
            </Text>
          </>
        )}
        hasMore={hasNextPage}
        isLoadingMore={isFetchingNextPage}
        isLoadingSuggestions={isFetching}
        itemRenderFn={(item, search) => (
          <UserDisplay search={search} user={item} showId />
        )}
        listType={listType}
        mx={-4}
        noItemsFound="No users found"
        searchPlaceholder="Search users or enter user ID"
        searchValue={searchValue}
        suggestions={suggestions}
        value={value}
        onChange={onChange}
        onListTypeChange={setListType}
        onLoadMore={fetchNextPage}
        onSearchInput={setSearchValue}
      />
    );
  },
);

UsersSelector.displayName = "UsersSelector";
